import React from "react";
import { IDSButton, IDSBadge, IDSAlert, IDSDialog, IDSLink } from "@inera/ids-react";

/**
 * Modal dialog (IDSDialog) shown when the user opens a contract in Testklient.
 * The user picks one of two modes before the test workflow starts:
 *
 *  - standard: run tests and submit a report for review
 *  - sandbox:  run tests freely, nothing is submitted
 *
 * When `inReview` is true the standard option is locked out and a plain-language
 * explanation is shown so non-technical users understand why.
 */
const ModeSelectionScreen = ({ contractName, documentationUrl, inReview, onSelect, onClose }) => {
  return (
    <IDSDialog
      show
      dismissible
      headline="Hur vill du genomföra testningen?"
      maxWidth="800px"
      onVisibilityChange={(visible) => {
        if (!visible) onClose?.();
      }}
    >
      <p className="ids-body" style={{ marginBottom: "0.5rem", color: "#555" }}>
        Välj ett alternativ för <strong>{contractName}</strong>.
      </p>

      {documentationUrl && (
        <p className="ids-body" style={{ marginBottom: "1rem", fontSize: "0.9rem" }}>
          <IDSLink underlined>
            <a href={documentationUrl} target="_blank" rel="noopener noreferrer">
              <span>
                Visa dokumentation för tjänstekontraktet
                <span aria-hidden="true" className="ids-icon-external-link-small ids-icon--text-end" />
              </span>
            </a>
          </IDSLink>
        </p>
      )}

      {/* Plain-language notice when a report is already under review */}
      {inReview && (
        <div style={{ marginBottom: "1.25rem" }}>
          <IDSAlert type="attention">
            <p>
              <strong>Du har redan skickat in ett underlag för det här kontraktet</strong> och
              det håller på att granskas av Inera. Tills granskningen är klar kan du inte
              skicka in ett nytt underlag. Du kan däremot fortfarande köra tester för att
              kontrollera att din miljö fungerar – välj då{" "}
              <em>Kör tester utan att skicka in</em> nedan.
            </p>
          </IDSAlert>
        </div>
      )}

      {/* Three-column card grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "1.25rem",
          marginTop: "0.5rem",
        }}
      >
        {/* ── Card 1: Standard (submit report) ── */}
        <div
          style={{
            border: `2px solid ${inReview ? "#d6d6d6" : "var(--ids-color-primary, #005b99)"}`,
            borderRadius: "8px",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            background: inReview ? "#f7f7f7" : "#fff",
            opacity: inReview ? 0.55 : 1,
          }}
        >
          <div style={{ fontSize: "2rem" }}>📋</div>

          <div>
            <h3 className="ids-heading-s" style={{ marginBottom: "0.4rem" }}>
              Testrapport
            </h3>
            <p
              className="ids-body"
              style={{ fontSize: "0.9rem", color: "#555", margin: 0 }}
            >
              Kör testfallen mot ditt system, granska resultaten, svara på ett par
              avslutande frågor och skicka in ett underlag för godkännande. Välj detta
              alternativ när du är redo att gå vidare i processen.
            </p>
          </div>

          {inReview ? (
            <p
              style={{
                fontSize: "0.85rem",
                color: "#888",
                margin: 0,
                fontStyle: "italic",
              }}
            >
              Inte tillgängligt just nu – ett inskickat underlag håller på att granskas.
            </p>
          ) : (
            <IDSButton
              size="m"
              onClick={() => onSelect("standard")}
              style={{ alignSelf: "flex-start", marginTop: "auto" }}
            >
              Välj detta alternativ
            </IDSButton>
          )}
        </div>

        {/* ── Card 2: Manual entry ── */}
        <div
          style={{
            border: `2px solid ${inReview ? "#d6d6d6" : "var(--ids-color-border-default, #d6d6d6)"}`,
            borderRadius: "8px",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            background: inReview ? "#f7f7f7" : "#fff",
            opacity: inReview ? 0.55 : 1,
          }}
        >
          <div style={{ fontSize: "2rem" }}>📝</div>

          <div>
            <h3 className="ids-heading-s" style={{ marginBottom: "0.4rem" }}>
              Självdeklaration
            </h3>
            <p
              className="ids-body"
              style={{ fontSize: "0.9rem", color: "#555", margin: 0 }}
            >
              Har du redan testat lokalt? Välj detta alternativ för att fylla i
              självdeklarationen steg för steg – du svarar på alla krav och frågor som
              gäller för tjänstekontraktet och skickar sedan in underlaget för granskning.
            </p>
          </div>

          {inReview ? (
            <p style={{ fontSize: "0.85rem", color: "#888", margin: 0, fontStyle: "italic" }}>
              Inte tillgängligt just nu – ett inskickat underlag håller på att granskas.
            </p>
          ) : (
            <IDSButton
              size="m"
              secondary
              onClick={() => onSelect("manual")}
              style={{ alignSelf: "flex-start", marginTop: "auto" }}
            >
              Välj detta alternativ
            </IDSButton>
          )}
        </div>
        
        {/* ── Card 3: Sandbox ── */}

        <div
          style={{
            border: "2px solid var(--ids-color-border-default, #d6d6d6)",
            borderRadius: "8px",
            padding: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            background: "#fff",
          }}
        >
          <div style={{ fontSize: "2rem" }}>🧪</div>

          <div>
            <h3 className="ids-heading-s" style={{ marginBottom: "0.4rem" }}>
              Labbmiljö
            </h3>
            <p
              className="ids-body"
              style={{ fontSize: "0.9rem", color: "#555", margin: 0 }}
            >
              Kör tester fritt för att prova verktyget, kontrollera din konfiguration
              eller felsöka. Inget skickas in och inga befintliga uppgifter påverkas.
              Perfekt när du vill utforska utan att det "räknas på riktigt".
            </p>
          </div>

          {inReview && <IDSBadge type="success">Tillgängligt nu</IDSBadge>}

          <IDSButton
            size="m"
            secondary
            onClick={() => onSelect("sandbox")}
            style={{ alignSelf: "flex-start", marginTop: "auto" }}
          >
            Välj detta alternativ
          </IDSButton>
        </div>

      </div>
    </IDSDialog>
  );
};

export default ModeSelectionScreen;
